import { useGraphQLClient } from "@package/graphql/react";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { logger } from "@artis/logger";
import { useEffect } from "react";
import { z } from "zod";

import {
  type AuthenticationDetails,
  posthogAttachAuth,
  datadogAttachAuth,
  sumologicAttachAuth,
  sentryAttachAuth,
  getDeploymentEnv,
} from "./integrations";
import { parseNumber } from "../../webapp/numbers";
import { ORG_BY_ID_QUERY } from "../../data/org";
import { readFragment } from "../../graphql";
import { useFeatureFlags } from "../flags";
import type { JWT } from "../../globals";
import { setDebugLogTags } from "./logger";
import { UserGroupsFragment, UserGroupsQuery } from "../../data/user/queries";
import { useAccessToken } from "../auth/hooks";

export { postHogIdentified } from "./integrations/posthog";

const DebugLogTagsSchema = z.object({ metadata: z.array(z.string()) });

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [debugLogTags] = useFeatureFlags("debug-logs");
  const graphql = useGraphQLClient();
  const token = useAccessToken();
  const auth = useAuth0();

  useEffect(() => {
    const tags = DebugLogTagsSchema.safeParse(debugLogTags);
    if (tags.success) setDebugLogTags(tags.data.metadata);
    else setDebugLogTags([]);
  }, [debugLogTags]);

  // Browser lifecycle — invaluable for diagnosing sleep/wake / network blip / tab restore.
  useEffect(() => {
    const onVisibilityChange = () => {
      logger.info("Browser visibility changed", { tags: ["lifecycle"], state: document.visibilityState });
    };
    const onFocus = () => logger.info("Browser window focused", { tags: ["lifecycle"] });
    const onBlur = () => logger.info("Browser window blurred", { tags: ["lifecycle"] });
    const onOnline = () => logger.info("Network online", { tags: ["lifecycle"] });
    const onOffline = () => logger.info("Network offline", { tags: ["lifecycle"] });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useQuery({
    queryKey: ["register-analytics", token.data?.access_token],
    queryFn: async () => {
      if (!token.data?.access_token) return null;
      try {
        const tkn = token.data?.access_token;
        const jwt = jwtDecode<JWT>(tkn);
        const hasuraOrgId = parseNumber(jwt?.["https://hasura.io/jwt/claims"]?.["x-hasura-org-id"]);
        const org = await graphql.execute(ORG_BY_ID_QUERY, { id: hasuraOrgId ?? 7 });

        const groupsQuery = await graphql.execute(UserGroupsQuery);
        const groups = readFragment(UserGroupsFragment, groupsQuery?.data?.user_groups);

        const details: AuthenticationDetails = {
          env: getDeploymentEnv(),
          user: {
            id: auth.user?.sub ?? "",
            email: auth.user?.email ?? "",
            given_name: auth.user?.given_name ?? "",
            family_name: auth.user?.family_name ?? "",
          },
          org: {
            id: hasuraOrgId ?? undefined,
            name: org.data?.organisation_by_pk?.sourceBySource.name,
            alias: org.data?.organisation_by_pk?.sourceBySource.alias ?? undefined,
            description: org.data?.organisation_by_pk?.sourceBySource.description ?? undefined,
          },
          origin: window.location.origin,
          version: import.meta.env.VITE_GIT_TAG || import.meta.env.VITE_GIT_SHA,
          tag: import.meta.env.VITE_GIT_TAG || "",
          branch: import.meta.env.VITE_GIT_BRANCH || "",
        };

        posthogAttachAuth({ ...details, groups: groups?.map((group) => group.id) ?? [] });
        datadogAttachAuth(details);
        sumologicAttachAuth(details);
        sentryAttachAuth(details);

        logger.withGlobalFields({
          ...details,
          browser: {
            agent: navigator?.userAgent,
            platform: navigator?.platform,
            language: navigator?.language,
          },
        });

        return null;
      } catch (error) {
        console.warn("[AnalyticsProvider] Failed to attach auth to analytics integrations:", error);
        return null;
      }
    },
  });

  return children;
};
